
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getUserSubFromUsernameCF,
  getUserProfileImageByUsernameCF,
  getUserProfileTextByUsernameCF,
  getUserPreferredNameByUsernameCF
} from '../utils/userProfileApi';
import { getIdToken } from '../utils/getIdToken';
import { getUserSub } from '../utils/getUserSub';
import {
  addFollower,
  deleteFollower,
  getUserFollowUsers,
  getUserFollowerUsers,
  checkBlockStatus,
  blockUser,
  unblockUser,
  sendFriendRequest,
  handleFriendRequest,
  checkFriendRequestStatus,
  deleteFriend,
  checkFriendStatus
} from '../utils/userFriendsHandle';
import { getFilteredUserNotes } from '../utils/getFilteredUserNotes';
import { loadTwitterScript } from '../components/renderNotes';
import { get_follow_friend_info } from '../utils/followerFriendsInfo';
import { Note, FollowFriendInfo } from '../types/types';
import {
  Typography,
  Box,
  Avatar,
  Button,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { getUserInfoByUsersubCF, UserData } from '../utils/userProfileApi';
import { useAppTokens } from '../context/ThemeModeContext';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { fetchPublicHighlightBooks, fetchPublicBookHighlights } from '../utils/highlightsApi';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { Highlight, HighlightBookItem } from '../types/rlTypes';
import OtherUsersBookshelfPanel from './OtherUsersBookShelfPage';
import OtherUsersListsPanel from './OtherUsersListsPage';

type ProfileTab = 'highlights' | 'bookshelf' | 'list';

const HEADER_HEIGHT = 56; // 全体共通Headerの高さ (mt:7 = 56px と揃える)

const UserPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { userNameOnURL } = useParams<{ userNameOnURL: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: ProfileTab = tabParam === 'bookshelf' || tabParam === 'list' ? tabParam : 'highlights';
  const handleTabChange = (_event: React.SyntheticEvent, newValue: ProfileTab) => {
    if (newValue === 'highlights') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ tab: newValue }, { replace: true });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 本棚・リストタブそれぞれの統計（各パネル内部で取得済みのものをコールバックで
  // 親に伝える）。ミニヘッダーの統計表示に使う。未取得の間はnullのまま、その項目を表示しない
  const [bookshelfStats, setBookshelfStats] = useState<{ shelfCount: number; bookCount: number } | null>(null);
  const [listStats, setListStats] = useState<{ listCount: number; bookCount: number } | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [userProfileImage, setUserProfileImage] = useState<string | null>(null);
  const [userProfileText, setUserProfileText] = useState<string | null>(null);
  const [preferredName, setPreferredName] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isFollowedBy, setIsFollowedBy] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isBlockedBy, setIsBlockedBy] = useState<boolean>(false);
  const [openUnfollowDialog, setOpenUnfollowDialog] = useState<boolean>(false);
  const [userSub, setUserSub] = useState<string | null>(null);
  const [targetUserSub, setTargetUserSub] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [accountNotFound, setAccountNotFound] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isFriend, setIsFriend] = useState<boolean>(false);
  const [friendRequestStatus, setFriendRequestStatus] = useState<'none' | 'pending'>('none');
  const [receivedRequestId, setReceivedRequestId] = useState<string | null>(null);
  const [followFriendsNum, setFollowFriendsNum] = useState<FollowFriendInfo>({
    following_sum: 0,
    follower_sum: 0,
    friends_sum: 0
  });

  // 公開ハイライト（抜粋・メモ）
  const [publicHighlightBooks, setPublicHighlightBooks] = useState<HighlightBookItem[]>([]);
  const [highlightBooksLoading, setHighlightBooksLoading] = useState<boolean>(true);
  // 右側に表示するハイライトの対象書籍。「一覧に戻る」を押しても null に戻さない
  // （戻るのはあくまで左パネルの表示切り替えのみで、右側の内容は最後に選んだ本のまま維持する）
  const [selectedHighlightBookId, setSelectedHighlightBookId] = useState<number | null>(null);
  // 左パネルを「一覧」と「選択中の本の書誌情報」のどちらで表示するか
  const [isShowingBookInfo, setIsShowingBookInfo] = useState<boolean>(false);
  const [bookHighlights, setBookHighlights] = useState<Highlight[]>([]);
  const [highlightsLoading, setHighlightsLoading] = useState<boolean>(false);

  const fetchUserProfile = useCallback(async (userName: string) => {
    try {
      const [imageResponse, textResponse, nameResponse] = await Promise.all([
        getUserProfileImageByUsernameCF(userName),
        getUserProfileTextByUsernameCF(userName),
        getUserPreferredNameByUsernameCF(userName)
      ]);

      setUserProfileImage(imageResponse || null);
      setUserProfileText(textResponse || null);
      setPreferredName(nameResponse || null);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, []);

  const fetchUserNotes = useCallback(async (targetSub: string, token: string | undefined) => {
    try {
      const fetchedNotes = await getFilteredUserNotes(targetSub, token);
      const sortedNotes = fetchedNotes?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) || null;
      setNotes(sortedNotes);
    } catch (error) {
      console.error('Error fetching user notes:', error);
    }
  }, []);

  const checkUserRelations = useCallback(async (sub: string | null, targetSub: string, token: string) => {
    if (sub && sub !== targetSub) {
      try {
        const [blockedByTarget, following, followedBy, friendStatus] = await Promise.all([
          checkBlockStatus(targetSub, sub, token),
          getUserFollowUsers(sub, token),
          getUserFollowerUsers(sub, token),
          checkFriendStatus(sub, targetSub, token)
        ]);

        setIsBlockedBy(blockedByTarget);
        setIsFollowing(following?.some(user => user.followedUserSub === targetSub) || false);
        setIsFollowedBy(followedBy?.some(user => user.followerUserSub === targetSub) || false);
        setIsFriend(friendStatus);

        if (!friendStatus) {
          const [friendStatusSent, friendStatusReceived] = await Promise.all([
            checkFriendRequestStatus(sub, targetSub, token),
            checkFriendRequestStatus(targetSub, sub, token)
          ]);
          
          if (friendStatusSent) {
            setFriendRequestStatus('pending');
          } else if (friendStatusReceived) {
            setFriendRequestStatus('pending');
            setReceivedRequestId(friendStatusReceived.requestId);
          } else {
            setFriendRequestStatus('none');
          }
        }
      } catch (error) {
        console.error('Error checking user relations:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = getIdToken();
        setIdToken(token);
        // console.log(token);
        // getUserSub()の失敗（セッション取得エラー等）は「閲覧者が誰か分からない」だけであり、
        // 「閲覧先アカウントが存在しない」こととは無関係。ここで例外を外側catchに伝播させると
        // 実在するアカウントでも誤って「アカウントが見つかりませんでした」表示になってしまう。
        let sub: string | null = null;
        try {
          sub = await getUserSub();
        } catch (error) {
          console.error('Error getting current user sub:', error);
        }
        setUserSub(sub);

        if (userNameOnURL) {
          const targetSub = await getUserSubFromUsernameCF(userNameOnURL);

          if (targetSub) {
            setTargetUserSub(targetSub);
            await Promise.all([
              fetchUserProfile(userNameOnURL),
              fetchUserNotes(targetSub, token || undefined),
              checkUserRelations(sub, targetSub, token!),

              // loadTwitterScript()

            ]);
            const followFriendsInfo = await get_follow_friend_info(targetSub, token || undefined);
            if (followFriendsInfo) {
              setFollowFriendsNum(followFriendsInfo);
            }
          } else {
            setAccountNotFound(true);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setAccountNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userNameOnURL, fetchUserProfile, fetchUserNotes, checkUserRelations]);

  // useEffect(() => {
  //   loadTwitterScript();
  //   console.log(notes);
  // }, [notes]);

  // 公開ハイライト付き書籍一覧を取得
  useEffect(() => {
    if (!targetUserSub) return;
    let cancelled = false;

    const load = async () => {
      setHighlightBooksLoading(true);
      try {
        const res = await fetchPublicHighlightBooks(targetUserSub);
        if (cancelled) return;
        const items: HighlightBookItem[] = res?.data?.items ?? [];
        setPublicHighlightBooks(items);
        if (items.length > 0) {
          setSelectedHighlightBookId((cur) => cur ?? items[0].rl_book_id);
        }
      } catch (error) {
        console.error('Error fetching public highlight books:', error);
      } finally {
        if (!cancelled) setHighlightBooksLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [targetUserSub]);

  // 選択中の書籍の公開ハイライト一覧を取得
  useEffect(() => {
    if (!targetUserSub || selectedHighlightBookId == null) {
      setBookHighlights([]);
      return;
    }
    let cancelled = false;

    const load = async () => {
      setHighlightsLoading(true);
      try {
        const res = await fetchPublicBookHighlights(targetUserSub, selectedHighlightBookId);
        if (cancelled) return;
        setBookHighlights(res?.data?.items ?? []);
      } catch (error) {
        console.error('Error fetching public highlights for book:', error);
      } finally {
        if (!cancelled) setHighlightsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [targetUserSub, selectedHighlightBookId]);

  const formatHighlightDate = (timestamp: number) => {
    // バックエンド側で既に日付単位(JST)に丸められている。表示側も時刻は出さない
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Tokyo',
    });
  };

  const handleFollowClick = async () => {
    if (isFollowing) {
      setOpenUnfollowDialog(true);
    } else {
      try {
        await addFollower(userSub!, targetUserSub!, idToken!);
        setIsFollowing(true);
        setFollowFriendsNum((prev: FollowFriendInfo) => ({ ...prev, follower_sum: prev.follower_sum + 1 }));
      } catch (error) {
        console.error('Error adding follow:', error);
      }
    }
  };

  const handleUnfollowConfirm = async () => {
    try {
      await deleteFollower(userSub!, targetUserSub!, idToken!);
      setIsFollowing(false);
      setOpenUnfollowDialog(false);
      setFollowFriendsNum((prev: FollowFriendInfo) => ({ ...prev, follower_sum: prev.follower_sum - 1 }));
    } catch (error) {
      console.error('Error deleting follow:', error);
    }
  };

  const handleBlockClick = async () => {
    try {
      if (isBlocked) {
        await unblockUser(userSub!, targetUserSub!, idToken!);
        setIsBlocked(false);
      } else {
        await blockUser(userSub!, targetUserSub!, idToken!);
        setIsBlocked(true);
      }
    } catch (error) {
      console.error('Error blocking/unblocking user:', error);
    }
  };

  const handleSendFriendRequest = async () => {
    try {
      await sendFriendRequest(userSub!, targetUserSub!, idToken!);
      setFriendRequestStatus('pending');
    } catch (error) {
      console.error('Error sending friend request:', error);
    }
  };

  const handleAcceptFriendRequest = async (requestId: string) => {
    try {
      await handleFriendRequest(requestId, 'accept', idToken!);
      setFriendRequestStatus('none');
      setIsFriend(true);
      setFollowFriendsNum((prev: FollowFriendInfo) => ({ ...prev, friends_sum: prev.friends_sum + 1 }));
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const handleRejectFriendRequest = async (requestId: string) => {
    try {
      await handleFriendRequest(requestId, 'reject', idToken!);
      setFriendRequestStatus('none');
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  const handleDeleteFriend = async () => {
    try {
      await deleteFriend(userSub!, targetUserSub!, idToken!);
      setFriendRequestStatus('none');
      setIsFriend(false);
      setFollowFriendsNum((prev: FollowFriendInfo) => ({ ...prev, friends_sum: prev.friends_sum - 1 }));
    } catch (error) {
      console.error('Error deleting friend:', error);
    }
  };

  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const [userInfos, setUserInfos] = useState<Record<string, UserData>>({});

  const fetchUserInfos = useCallback(async () => {
    if (notes) {
      setLoadingUserInfo(true);
      const uniqueUserSubs = Array.from(new Set(notes.map(note => note.userSub)));

      
      const userInfoPromises = uniqueUserSubs.map(async (userSub) => {
        const userInfo = await getUserInfoByUsersubCF(userSub);
        return { userSub, userInfo };
      });

      const userInfosArray = await Promise.all(userInfoPromises);

      const userInfosObject = userInfosArray.reduce((acc, item) => {
        if (item && item.userInfo) {
          acc[item.userSub] = item.userInfo;
        }
        return acc;
      }, {} as Record<string, UserData>);

      setUserInfos(userInfosObject);
      setLoadingUserInfo(false);
    }
  }, [notes]);


  useEffect(() => {
    if (notes) {
      fetchUserInfos();
      loadTwitterScript();
    }
  }, [notes, fetchUserInfos]);


  const navigate = useNavigate();

  const tabsSx = {
    minHeight: 0,
    '& .MuiTab-root': {
      minHeight: 44,
      fontSize: 13,
      fontWeight: 700,
      textTransform: 'none' as const,
      color: TOKENS.textSub,
    },
    '& .Mui-selected': { color: `${TOKENS.text} !important` },
    '& .MuiTabs-indicator': { background: TOKENS.accent },
  } as const;

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress size={28} sx={{ color: TOKENS.textSub }} />
        </Box>
      ) : accountNotFound ? (
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.text, textAlign: 'center', mt: 6 }}>
          アカウントが見つかりませんでした。
        </Typography>
      ) : (
        <>
          {/* サイト共通ヘッダーの下にもう1段、常時表示のミニヘッダー。ユーザー名+簡易統計。
              スクロールしても画面上部に残り続け、ユーザー名クリックで先頭へスクロール */}
          <Box
            sx={{
              position: 'sticky',
              top: HEADER_HEIGHT,
              zIndex: 1150,
              background: TOKENS.bg,
              borderBottom: `1px solid ${TOKENS.border}`,
            }}
          >
            <Box sx={{ maxWidth: 640, margin: 'auto', px: 2 }}>
              <Button
                onClick={scrollToTop}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  textTransform: 'none',
                  pt: 1.5,
                  pb: 1,
                  px: 0,
                  minWidth: 0,
                  '&:hover': { background: 'transparent' },
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text, lineHeight: 1.3 }}>
                  {preferredName || `@${userNameOnURL}`}
                </Typography>
                {(() => {
                  const parts: string[] = [];
                  if (!highlightBooksLoading) {
                    const totalHighlights = publicHighlightBooks.reduce((sum, b) => sum + (b.highlight_count ?? 0), 0);
                    parts.push(`抜粋・メモ:${publicHighlightBooks.length}冊${totalHighlights}件`);
                  }
                  if (bookshelfStats) parts.push(`本棚:${bookshelfStats.shelfCount}棚${bookshelfStats.bookCount}冊`);
                  if (listStats) parts.push(`リスト:${listStats.listCount}リスト${listStats.bookCount}冊`);
                  if (parts.length === 0) return null;
                  return (
                    <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 0.25 }}>
                      {parts.join('　')}
                    </Typography>
                  );
                })()}
              </Button>
            </Box>
          </Box>

          <Box sx={{ maxWidth: 640, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
            {/* コンパクトなプロフィールカード */}
            <Box
              sx={{
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.glass,
                borderRadius: '20px',
                p: 2,
                display: 'flex',
                gap: 2,
              }}
            >
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  alt="Profile Image"
                  src={userProfileImage || undefined}
                  sx={{
                    width: 64,
                    height: 64,
                    border: `2px solid ${TOKENS.borderSoft}`,
                  }}
                />
                {userSub === targetUserSub && (
                  <Button
                    component={Link}
                    to="/profile"
                    sx={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      minWidth: 0,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: TOKENS.buttonBg,
                      border: `1px solid ${TOKENS.border}`,
                      color: TOKENS.text,
                      '&:hover': { background: TOKENS.buttonBgHover }
                    }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 12 }} />
                  </Button>
                )}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: TOKENS.text, lineHeight: 1.3 }}>
                  {preferredName}
                </Typography>
                <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mt: 0.25 }}>
                  @{userNameOnURL}
                </Typography>
                {userProfileText && (
                  <Typography sx={{ mt: 1, fontSize: 13, color: TOKENS.text, whiteSpace: 'pre-wrap' }}>
                    {userProfileText}
                  </Typography>
                )}
                {isFollowedBy && (
                  <Typography sx={{ mt: 1, fontSize: 12, color: TOKENS.textSub }}>
                    このユーザーはあなたをフォローしています。
                  </Typography>
                )}
                {userSub === targetUserSub && (
                  <Button
                    component={Link}
                    to="/profile"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontSize: 12,
                      color: TOKENS.textSub,
                      minWidth: 0,
                      p: 0,
                      '&:hover': { color: TOKENS.text, background: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    プロフィール編集
                  </Button>
                )}
              </Box>
            </Box>

            {/* 通常表示のタブバー */}
            <Box sx={{ mt: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ ...tabsSx, borderBottom: `1px solid ${TOKENS.border}` }}>
                <Tab label="抜粋・メモ" value="highlights" />
                <Tab label="本棚" value="bookshelf" />
                <Tab label="リスト" value="list" />
              </Tabs>
            </Box>
          </Box>

          {/* タブの中身は本棚・リストのグリッド表示のため、プロフィールカードより広い幅を使う */}
          <Box sx={{ maxWidth: 1100, margin: 'auto', px: 2, pb: { xs: 3, md: 4 } }}>
            <Box sx={{ mt: 2 }}>
              {activeTab === 'highlights' && (
                highlightBooksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={24} sx={{ color: TOKENS.textSub }} />
                  </Box>
                ) : publicHighlightBooks.length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 6 }}>
                    公開されている抜粋・メモがありません。
                  </Typography>
                ) : (() => {
                  const selectedBook = publicHighlightBooks.find(b => b.rl_book_id === selectedHighlightBookId);
                  return (
          <Box
            sx={{
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.glass,
              borderRadius: '20px',
              overflow: 'hidden',
              display: 'flex',
              height: 420
            }}
          >
            {/* 左：公開ハイライトのある本の一覧（狭め・左寄り）。本を選択すると、
                一覧の代わりにその本の書誌情報（書影・タイトル・責任表示・出版年・出版社）を表示する */}
            <Box
              sx={{
                width: 180,
                flexShrink: 0,
                borderRight: `1px solid ${TOKENS.border}`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              {(() => {
                if (isShowingBookInfo && selectedBook) {
                  const cover = generateCoverUrlSmart(selectedBook.rl_book_id, selectedBook.title, selectedBook.isbn13);
                  return (
                    <>
                      <Button
                        fullWidth
                        onClick={() => setIsShowingBookInfo(false)}
                        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: 0,
                          borderBottom: `1px solid ${TOKENS.border}`,
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontSize: 12,
                          fontWeight: 600,
                          color: TOKENS.text,
                          py: 1,
                          px: 1.5,
                          flexShrink: 0,
                          '&:hover': { background: 'rgba(148,163,184,0.08)' }
                        }}
                      >
                        一覧に戻る
                      </Button>
                      <Box sx={{ p: 1.5, overflowY: 'auto' }}>
                        <Box
                          component="a"
                          href={`/book/${selectedBook.rl_book_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'block' }}
                        >
                          <Box
                            component="img"
                            src={cover}
                            alt={selectedBook.title ?? ''}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1.42',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              background: TOKENS.cardBg,
                              cursor: 'pointer'
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.text, lineHeight: 1.35, mt: 1 }}>
                          {selectedBook.title ?? `書籍 #${selectedBook.rl_book_id}`}
                        </Typography>
                        {selectedBook.author && (
                          <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 0.5 }}>
                            {selectedBook.author}
                          </Typography>
                        )}
                        {selectedBook.pub_year != null && (
                          <Typography sx={{ fontSize: 11, color: TOKENS.textSub, mt: 0.5 }}>
                            {selectedBook.pub_year}
                          </Typography>
                        )}
                        {selectedBook.publisher && (
                          <Typography sx={{ fontSize: 11, color: TOKENS.textSub, mt: 0.5 }}>
                            {selectedBook.publisher}
                          </Typography>
                        )}
                      </Box>
                    </>
                  );
                }
                return (
                  <>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: TOKENS.textSub, px: 1.5, pt: 1.5, pb: 1 }}>
                      抜粋・メモ
                    </Typography>
                    <List sx={{ py: 0, flexGrow: 1, overflowY: 'auto' }}>
                      {publicHighlightBooks.map((book) => {
                        const isSelected = selectedHighlightBookId === book.rl_book_id;
                        const bookCover = generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
                        return (
                          <ListItemButton
                            key={book.rl_book_id}
                            selected={isSelected}
                            onClick={() => {
                              setSelectedHighlightBookId(book.rl_book_id);
                              setIsShowingBookInfo(true);
                            }}
                            sx={{
                              px: 1.5,
                              py: 1,
                              gap: 1,
                              alignItems: 'flex-start',
                              borderLeft: isSelected ? `3px solid ${TOKENS.accent}` : '3px solid transparent',
                              '&.Mui-selected': { background: 'rgba(148,163,184,0.10)' },
                              '&.Mui-selected:hover': { background: 'rgba(148,163,184,0.14)' },
                              '&:hover': { background: 'rgba(148,163,184,0.08)' }
                            }}
                          >
                            <Box
                              component="img"
                              src={bookCover}
                              alt={book.title ?? ''}
                              sx={{ width: 32, height: 44, objectFit: 'contain', borderRadius: '4px', flexShrink: 0, background: TOKENS.cardBg }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: TOKENS.text,
                                  fontWeight: isSelected ? 700 : 500,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.3
                                }}
                              >
                                {book.title ?? `書籍 #${book.rl_book_id}`}
                              </Typography>
                              <Typography sx={{ fontSize: 10.5, color: TOKENS.textSub, mt: 0.25 }}>
                                {book.highlight_count} 件
                              </Typography>
                            </Box>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </>
                );
              })()}
            </Box>

            {/* 右：選択中の本の公開ハイライト（新しい順）。上部のミニヘッダー（書影＋タイトル）は
                スクロールに追従させず常に固定表示する */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {selectedBook && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minWidth: 0,
                    flexShrink: 0,
                    px: 2,
                    py: 1.25,
                    borderBottom: `1px solid ${TOKENS.border}`
                  }}
                >
                  <Box
                    component="img"
                    src={generateCoverUrlSmart(selectedBook.rl_book_id, selectedBook.title, selectedBook.isbn13)}
                    alt={selectedBook.title ?? ''}
                    sx={{ width: 16, height: 22, objectFit: 'contain', borderRadius: '2px', flexShrink: 0, background: TOKENS.cardBg }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: TOKENS.textSub,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0
                    }}
                  >
                    {selectedBook.title ?? `書籍 #${selectedBook.rl_book_id}`}
                  </Typography>
                </Box>
              )}
              <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
              {highlightsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress size={20} sx={{ color: TOKENS.textSub }} />
                </Box>
              ) : bookHighlights.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  公開されている抜粋・メモはありません。
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {bookHighlights.map((highlight) => (
                    <Box
                      key={highlight.rl_highlight_id}
                      sx={{
                        background: TOKENS.cardBg,
                        border: `1px solid ${TOKENS.border}`,
                        borderRadius: 2,
                        p: 1.5
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: TOKENS.text,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {highlight.text}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {highlight.page_number != null && (
                          <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>
                            p.{highlight.page_number}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>
                          {formatHighlightDate(highlight.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              </Box>
            </Box>
          </Box>
                  );
                })()
              )}
              {/* 本棚・リストタブはタブ切り替えのたびに再取得しないよう、常時マウントして
                  display切り替えのみで表示・非表示する（状態とデータをタブ間で保持する） */}
              {userNameOnURL && (
                <Box sx={{ display: activeTab === 'bookshelf' ? 'block' : 'none' }}>
                  <OtherUsersBookshelfPanel userNameOnURL={userNameOnURL} onStatsLoaded={setBookshelfStats} />
                </Box>
              )}
              {userNameOnURL && (
                <Box sx={{ display: activeTab === 'list' ? 'block' : 'none' }}>
                  <OtherUsersListsPanel userNameOnURL={userNameOnURL} onStatsLoaded={setListStats} />
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
export default UserPage;